import { useState, useCallback } from 'react';
import { apiJson } from '../lib/api';
import { useAuthContext } from '../contexts/AuthContext';
import { toUserMessage } from '../lib/apiError';

export interface SendEmailData {
  to: string;
  subject: string;
  html?: string;
  text?: string;
  cc?: string[];
  bcc?: string[];
}

export interface SendTemplateEmailData {
  to: string;
  templateId: string;
  data?: Record<string, unknown>;
  cc?: string[];
  bcc?: string[];
}

export interface EmailResponse {
  id: string;
  success: boolean;
  error?: string;
}

export interface EmailStatusResponse {
  id: string;
  status: 'sent' | 'delivered' | 'bounced' | 'complained' | 'pending';
  createdAt: string;
  lastEventAt?: string;
  to?: string;
  subject?: string;
}

export function useEmail() {
  const { session } = useAuthContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendEmail = useCallback(async (data: SendEmailData): Promise<EmailResponse | null> => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiJson<EmailResponse>('/api/email/send', {
        method: 'POST',
        body: JSON.stringify(data),
      }, session?.access_token);
      return result;
    } catch (err) {
      const message = toUserMessage(err, "We couldn't reach the server. Please check your connection and try again.");
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [session?.access_token]);

  const sendTemplateEmail = useCallback(
    async (data: SendTemplateEmailData): Promise<EmailResponse | null> => {
      setLoading(true);
      setError(null);

      try {
        const result = await apiJson<EmailResponse>('/api/email/send-template', {
          method: 'POST',
          body: JSON.stringify(data),
        }, session?.access_token);
        return result;
      } catch (err) {
        const message = toUserMessage(err, "We couldn't reach the server. Please check your connection and try again.");
        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [session?.access_token]
  );

  const getEmailStatus = useCallback(
    async (emailId: string): Promise<EmailStatusResponse | null> => {
      setLoading(true);
      setError(null);

      try {
        const result = await apiJson<EmailStatusResponse>(
          `/api/email/status/${emailId}`,
          undefined,
          session?.access_token
        );
        return result;
      } catch (err) {
        const message = toUserMessage(err, "We couldn't reach the server. Please check your connection and try again.");
        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [session?.access_token]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    sendEmail,
    sendTemplateEmail,
    getEmailStatus,
    loading,
    error,
    clearError,
  };
}
